import axios from 'axios';
import store from '@/redux/store';
import { setShowLoader } from '@/redux/slices/loaderSlice';
import {
  showWarningModal,
  showErrorModal,
  showExpiredSessionModal,
} from '@/redux/slices/modalAlertSlice';
import { msalInstance } from './msal';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { addOriginGuard } from './axiosOriginGuard';

let apiController = new AbortController();
const api = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_URL,
});

addOriginGuard(api);

api.defaults.headers.common = {
  'Content-Encoding': 'gzip',
};

api.interceptors.request.use((config) => {
  store.dispatch(setShowLoader(true));
  const account = msalInstance.getAllAccounts()[0];
  return msalInstance
    .acquireTokenSilent({
      scopes: [import.meta.env.VITE_AZUREAD_CLIENTID],
      account: account,
    })
    .then((response) => {
      const accessToken = response.accessToken;
      config.headers.Authorization = `Bearer ${accessToken}`;
      return config;
    })
    .catch((error) => {
      if (error instanceof InteractionRequiredAuthError) {
        msalInstance.acquireTokenRedirect({
          scopes: [import.meta.env.VITE_AZUREAD_CLIENTID],
          account,
        });
      } else {
        store.dispatch(showExpiredSessionModal(true));
      }
      if (import.meta.env.DEV) {
        console.error('Error acquiring token:', error);
      }
      return Promise.reject(error);
    });
});

api.interceptors.response.use(
  (response) => {
    if (response.status === 200 || response.status === 201) {
      if (response.data.success === 0) {
        store.dispatch(showWarningModal('', response.data.message));
      }
    }
    store.dispatch(setShowLoader(false));
    return response;
  },
  (error) => {
    store.dispatch(setShowLoader(false));
    const requestUrl = String(error?.config?.url || '');
    const shouldSilenceSewerAnalysis500 =
      error?.response?.status === 500 &&
      requestUrl.includes('kepler/sewer-analysis/location/');

    if (!shouldSilenceSewerAnalysis500) {
      console.error('error', error);
    }

    if (error.code === 'ERR_NETWORK') {
      window.location.href = '/error';
    } else if (
      error.code === 'ECONNABORTED' &&
      error.message.includes('timeout')
    ) {
      window.location.href = '/error';
    } else if (error.response && error.response.status === 429) {
      store.dispatch(
        showErrorModal(
          'You have reached the limit of requests to the server. Retry in a few minutes.',
          error.response?.data?.error,
        ),
      );
    } else if (error.code === 'ERR_CANCELED') {
    } else if (shouldSilenceSewerAnalysis500) {
    } else if (error.response && error.response.status === 500) {
      store.dispatch(
        showErrorModal(error.message, `Sorry for the disruption—an unexpected error occurred on our side. 
          We've logged the details and our team is investigating. 
          Please try refreshing the page; if it continues, please contact us at`, true),
      );
    } else {
      store.dispatch(
        showWarningModal(
          error.response?.data?.type || 'Warning',
          error.response?.data?.message ||
            error.response?.data?.body?.message ||
            'An error occurred',
        ),
      );
    }

    return Promise.reject(error);
  },
);

const restartApiCotroller = () => {
  apiController = new AbortController();
};

export { api, apiController, restartApiCotroller };
